import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";
import type {
  ExposureRecord,
  GasReading,
  IncidentDetail,
  IncidentEvent,
  IncidentPage,
  IncidentReportDraft,
  IncidentReportV1,
  JobRef,
} from "@/api/models";
import { toGasReadings, toIncidentSummary } from "@/api/models";
import { trackEvent, AnalyticsEvent } from "@/analytics";

export type {
  ExposureRecord,
  GasReading,
  IncidentDetail,
  IncidentEvent,
  IncidentPage,
  IncidentReportDraft,
  IncidentReportV1,
};

export type IncidentsListParams = {
  status?: "open" | "closed" | "all";
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  sort?: "dispatched_at_desc" | "dispatched_at_asc" | "cleared_at_desc";
};

export function useIncidents(params: IncidentsListParams = {}) {
  // Scope the cache by the active department: a respai_admin switching
  // departments must not see the previous tenant's incidents from cache. The
  // request is already dept-scoped via the X-RespAi-Department-ID header, so the
  // key must carry the same value or two departments collide on one cache entry.
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["incidents", deptId, params],
    queryFn: async (): Promise<IncidentPage> => {
      const rows = (
        await api.get<components["schemas"]["IncidentRead"][]>("/incidents")
      ).map(toIncidentSummary);
      const filtered = rows
        .filter((i) => {
          if (!params.status || params.status === "all") return true;
          const isOpen = i.status === "active";
          return params.status === "open" ? isOpen : !isOpen;
        })
        .filter((i) => (params.from ? i.dispatched_at >= params.from : true))
        .filter((i) => (params.to ? i.dispatched_at <= params.to : true))
        .sort((a, b) =>
          params.sort === "dispatched_at_asc"
            ? a.dispatched_at.localeCompare(b.dispatched_at)
            : (b.cleared_at ?? b.dispatched_at).localeCompare(
                a.cleared_at ?? a.dispatched_at,
              ),
        );
      const page = params.page ?? 1;
      const pageSize = params.pageSize ?? 25;
      return {
        items: filtered.slice((page - 1) * pageSize, page * pageSize),
        total: filtered.length,
        page,
        page_size: pageSize,
      };
    },
    staleTime: 30 * 1000,
  });
}

export function useIncident(id: string | undefined) {
  return useQuery({
    queryKey: ["incident", id],
    queryFn: async (): Promise<IncidentDetail> => {
      const incident = await api.get<components["schemas"]["IncidentRead"]>(
        `/incidents/${id}`,
      );
      // The detail route enriches with IC + device serial (derived server-side);
      // carry them through instead of blanking, so the Metadata card populates.
      return {
        ...toIncidentSummary(incident),
        ic_display_name: incident.ic_display_name ?? null,
        sensor_device_serial: incident.sensor_device_serial ?? null,
      };
    },
    enabled: !!id,
  });
}

export type IncidentMap = {
  url: string;
  expires_at: string;
  bounds: components["schemas"]["GeoBounds"] | null;
};

/**
 * Signed map-snapshot URL for an incident. The snapshot lives behind a separate
 * endpoint (auto-generated from the incident lat/lon), not on the incident row,
 * so a missing location / snapshot is a 404 we render as "No map" rather than an
 * error. ``bounds`` is the snapshot's geographic extent (N/S/E/W) — carried
 * through so the overlay can project firefighter dots onto the image exactly.
 */
export function useIncidentMap(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-map", id],
    queryFn: async (): Promise<IncidentMap | null> => {
      try {
        const res = await api.get<
          components["schemas"]["IncidentMapResponse"]
        >(`/incidents/${id}/map`);
        return {
          url: res.url,
          expires_at: res.expires_at,
          bounds: res.bounds ?? null,
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: !!id,
    retry: false,
  });
}

/**
 * Latest fix per firefighter for the incident — the *static* after-action
 * snapshot (one frozen dot per responder), not a live feed. Backed by the
 * department-scoped ``/positions/last-per-user`` endpoint (require_ic_or_admin),
 * so a chief reviewing a closed incident can load it. Returns [] on 404 / no
 * fixes rather than erroring, since an incident may simply have no positions.
 */
export function useIncidentLastPositions(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-last-positions", id],
    queryFn: async (): Promise<
      components["schemas"]["PositionRead"][]
    > => {
      try {
        return await api.get<components["schemas"]["PositionRead"][]>(
          `/incidents/${id}/positions/last-per-user`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!id,
    retry: false,
  });
}

export function useIncidentEvents(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-events", id],
    queryFn: async (): Promise<IncidentEvent[]> =>
      (
        await api.get<components["schemas"]["EventRead"][]>(
          `/incidents/${id}/events`,
        )
      ).map((e) => ({
        ...e,
        kind: e.event_type,
        ts: e.occurred_at,
        actor_display_name: null,
      })),
    enabled: !!id,
  });
}

export function useIncidentGasReadings(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-gas", id],
    // After-action review uses the department-scoped historical endpoint
    // (require_ic_or_admin). The bare /gas-readings feed is live, assigned-IC
    // only (WS-C), which would 403 a chief reviewing a closed incident.
    queryFn: async (): Promise<GasReading[]> =>
      (
        await api.get<components["schemas"]["GasReadingRead"][]>(
          `/incidents/${id}/gas-readings/history`,
        )
      ).flatMap(toGasReadings),
    enabled: !!id,
  });
}

export function useIncidentExposure(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-exposure", id],
    // One exposure record per firefighter per incident — return ALL of them, not
    // just the first match, so the after-action view shows the whole crew. The
    // list is already department-scoped by RLS.
    queryFn: async (): Promise<
      components["schemas"]["ExposureRecordRead"][]
    > => {
      const rows =
        await api.get<components["schemas"]["ExposureRecordRead"][]>(
          "/exposure-records",
        );
      return rows.filter((r) => r.incident_id === id);
    },
    enabled: !!id,
  });
}

export function useIncidentReport(id: string | undefined) {
  return useQuery({
    queryKey: ["incident-report", id],
    queryFn: async (): Promise<IncidentReportDraft | null> => {
      const report = await api.get<components["schemas"]["ReportRead"]>(
        `/incidents/${id}/report`,
      );
      const draft = report.draft_json as Partial<IncidentReportV1>;
      return {
        ...report,
        body: {
          hazards_identified: draft.hazards_identified ?? [],
          decon_actions: draft.decon_actions ?? [],
          gaps: draft.gaps ?? [],
          after_action_notes: draft.after_action_notes ?? "",
          ...draft,
        },
        markdown: null,
        pdf_url: report.pdf_s3_key ?? null,
      };
    },
    enabled: !!id,
  });
}

export function useUpdateIncidentReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IncidentReportV1) =>
      api.patch<IncidentReportDraft>(`/incidents/${id}/report`, {
        draft_json: body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident-report", id] });
    },
  });
}

export function useFinalizeIncidentReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<JobRef>(`/incidents/${id}/report/finalize`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident-report", id] });
    },
  });
}

/**
 * Fetch a presigned URL for the report PDF on demand. The draft stores only the
 * S3 object key; opening that bare key as a link navigated the SPA to a dead
 * route (which fell back to the Overview page). Resolve a real signed URL from
 * the API instead, then open it. Returns 404 until the PDF has been rendered.
 *
 * Pass `{ download: true }` to the mutation to get an `attachment`-flavored URL
 * (the API presigns it with `Content-Disposition: attachment`), so navigating
 * to it saves the file instead of opening the PDF inline in a new tab. The
 * default (inline) URL is what the in-page iframe viewer wants.
 */
export function useIncidentReportPdfUrl(id: string) {
  return useMutation({
    mutationFn: (opts?: { download?: boolean }) =>
      api.get<{ url: string; expires_at: string }>(
        `/incidents/${id}/report/pdf${opts?.download ? "?download=true" : ""}`,
      ),
    onSuccess: (_data, opts) => {
      trackEvent(
        opts?.download
          ? AnalyticsEvent.ReportPdfDownloaded
          : AnalyticsEvent.ReportPdfViewed,
      );
    },
  });
}
